import { makeAutoObservable, runInAction } from "mobx";
import FirebaseService from "./FirebaseService";
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  addDoc
} from "firebase/firestore";
import { 
  ref, 
  uploadString, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { StravaActivity, StravaActivityStreams } from "./StravaService";
import posthog from "posthog-js";

export interface Comparison {
  id?: string;
  planId: string;
  userId: string; // User ID who owns this comparison
  activityData: string;  // Stringified JSON of Strava activity
  streamsStoragePath?: string; // Path to streams data in Firebase Storage
  createdAt: Date;
  updatedAt: Date;
}

export default class ComparisonsService {
  private _firebaseService: FirebaseService;
  private _isLoading: boolean = false;
  private _error: string | null = null;
  private _cachedStreams: Map<string, StravaActivityStreams> = new Map(); // In-memory cache

  constructor(firebaseService: FirebaseService) {
    this._firebaseService = firebaseService;
    makeAutoObservable(this);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get error(): string | null {
    return this._error;
  }

  /**
   * Get a comparison by its ID
   * @param comparisonId Comparison ID
   * @param userId User ID for analytics tracking
   */
  async getComparison(comparisonId: string, userId: string): Promise<Comparison | null> {
    this._isLoading = true;
    this._error = null;

    try {
      const db = this._firebaseService.db;
      const comparisonRef = doc(db, 'comparisons', comparisonId);
      const snapshot = await getDoc(comparisonRef);

      if (snapshot.exists()) {
        const data = snapshot.data();
        const comparison = {
          id: snapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Comparison;

        posthog.capture('comparison_viewed', { 
          comparison_id: comparisonId,
          plan_id: comparison.planId,
          user_id: userId
        });
        
        runInAction(() => {
          this._isLoading = false;
        });
        
        return comparison;
      }

      runInAction(() => {
        this._isLoading = false;
      });

      return null; // No comparison found
    } catch (error) {
      console.error('Error getting comparison:', error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture('error', { 
        source: 'getComparison', 
        error: (error as Error).message,
        comparison_id: comparisonId,
        user_id: userId
      });
      return null;
    }
  }

  /**
   * Get a comparison by plan ID
   * @param planId Plan ID to find the comparison for
   * @param userId User ID for storage path and analytics
   */
  async getComparisonByPlanId(planId: string, userId: string): Promise<Comparison | null> {
    this._isLoading = true;
    this._error = null;

    try {
      const db = this._firebaseService.db;
      const comparisonsRef = collection(db, 'comparisons');
      const q = query(comparisonsRef, where('planId', '==', planId), where('userId', '==', userId));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        // Should only be one comparison per plan
        const doc = snapshot.docs[0];
        const data = doc.data();
        const comparison = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Comparison;

        posthog.capture('comparison_viewed_by_plan', { 
          comparison_id: doc.id,
          plan_id: planId,
          user_id: userId
        });
        
        runInAction(() => {
          this._isLoading = false;
        });
        
        return comparison;
      }

      runInAction(() => {
        this._isLoading = false;
      });

      return null; // No comparison found for this plan
    } catch (error) {
      console.error('Error getting comparison by plan ID:', error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture('error', { 
        source: 'getComparisonByPlanId', 
        error: (error as Error).message,
        plan_id: planId,
        user_id: userId
      });
      return null;
    }
  }

  /**
   * Create or update a comparison
   * @param planId Plan ID
   * @param userId User ID of the comparison owner
   * @param activity Strava activity
   * @param existingComparisonId Optional ID of existing comparison to update
   * @param streams Optional activity streams data
   * @returns The created/updated comparison
   */
  async saveComparison(
    planId: string,
    userId: string,
    activity: StravaActivity,
    existingComparisonId?: string,
    streams?: StravaActivityStreams
  ): Promise<Comparison | null> {
    this._isLoading = true;
    this._error = null;

    try {
      const db = this._firebaseService.db;
      const storage = this._firebaseService.storage;
      const now = new Date();

      // Check for existing comparison
      let existingComparison = null;
      if (existingComparisonId) {
        const comparisonRef = doc(db, 'comparisons', existingComparisonId);
        const comparisonSnap = await getDoc(comparisonRef);
        if (comparisonSnap.exists()) {
          const data = comparisonSnap.data();
          existingComparison = {
            id: comparisonSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Comparison;
        }
      } else {
        existingComparison = await this.getComparisonByPlanId(planId, userId);
      }

      // Prepare comparison data
      const comparisonData: Omit<Comparison, 'id'> = {
        planId,
        userId,
        activityData: JSON.stringify(activity),
        createdAt: existingComparison?.createdAt || now,
        updatedAt: now
      };

      // Store streams in user-specific location in Cloud Storage if provided
      if (streams) {
        // Clear previous cached streams if they exist
        if (existingComparison?.id) {
          this._cachedStreams.delete(existingComparison.id);
        }

        let streamsStoragePath: string;
        
        if (existingComparison?.id) {
          // Use existing path but ensure it's under the user's directory
          streamsStoragePath = `usersFiles/${userId}/comparisons/${existingComparison.id}/streams.json`;
        } else {
          // Create a temporary path until we have the document ID
          const tempId = Math.random().toString(36).substring(2, 15);
          streamsStoragePath = `usersFiles/${userId}/comparisons/${tempId}/streams.json`;
        }
        
        // Upload streams to Cloud Storage
        const streamsRef = ref(storage, streamsStoragePath);
        await uploadString(streamsRef, JSON.stringify(streams), 'raw');
        
        // Store the storage path in the comparison document
        comparisonData.streamsStoragePath = streamsStoragePath;
      }

      let comparisonId: string;
      let eventName: string;

      if (existingComparison) {
        // Update existing comparison
        comparisonId = existingComparison.id!;
        const comparisonRef = doc(db, 'comparisons', comparisonId);
        
        // If the streams path has changed and there was an old one, delete the old file
        if (existingComparison.streamsStoragePath && 
            comparisonData.streamsStoragePath && 
            existingComparison.streamsStoragePath !== comparisonData.streamsStoragePath) {
          const oldStreamsRef = ref(storage, existingComparison.streamsStoragePath);
          try {
            await deleteObject(oldStreamsRef);
          } catch (error) {
            console.warn('Error deleting old streams file:', error);
            // Continue with the update even if old file deletion fails
          }
        }
        
        await setDoc(comparisonRef, {
          ...comparisonData,
          createdAt: Timestamp.fromDate(comparisonData.createdAt),
          updatedAt: Timestamp.fromDate(now)
        });
        
        eventName = 'comparison_updated';
      } else {
        // Create new comparison
        const comparisonsRef = collection(db, 'comparisons');
        const docRef = await addDoc(comparisonsRef, {
          ...comparisonData,
          createdAt: Timestamp.fromDate(now),
          updatedAt: Timestamp.fromDate(now)
        });
        
        comparisonId = docRef.id;
        
        // If we've stored streams with a temporary path, we need to move them to the final path
        if (comparisonData.streamsStoragePath && comparisonData.streamsStoragePath.includes('comparisons/')) {
          // Extract the temporary ID from the path
          const tempIdMatch = comparisonData.streamsStoragePath.match(/comparisons\/(.+?)\/streams/);
          if (tempIdMatch && tempIdMatch[1]) {
            const tempId = tempIdMatch[1];
            const tempPath = comparisonData.streamsStoragePath;
            const newPath = tempPath.replace(tempId, comparisonId);
            
            // Create a reference to the file to copy
            const oldRef = ref(storage, tempPath);
            const newRef = ref(storage, newPath);
            
            // Copy the file to the new location
            const downloadUrl = await getDownloadURL(oldRef);
            const response = await fetch(downloadUrl);
            const streamsText = await response.text();
            await uploadString(newRef, streamsText, 'raw');
            
            // Delete the old file
            await deleteObject(oldRef);
            
            // Update the path in Firestore
            await setDoc(doc(db, 'comparisons', comparisonId), {
              streamsStoragePath: newPath
            }, { merge: true });
            
            // Update the return value
            comparisonData.streamsStoragePath = newPath;
          }
        }
        
        eventName = 'comparison_created';
      }

      const result: Comparison = {
        ...comparisonData,
        id: comparisonId
      };

      posthog.capture(eventName, { 
        comparison_id: comparisonId,
        plan_id: planId,
        activity_id: activity.id,
        user_id: userId,
        has_streams: !!streams
      });

      runInAction(() => {
        this._isLoading = false;
      });

      return result;
    } catch (error) {
      console.error('Error saving comparison:', error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture('error', { 
        source: 'saveComparison', 
        error: (error as Error).message,
        plan_id: planId,
        user_id: userId
      });
      return null;
    }
  }

  /**
   * Delete a comparison
   * @param comparisonId Comparison ID to delete
   * @param userId User ID for analytics tracking
   */
  async deleteComparison(comparisonId: string, userId: string): Promise<boolean> {
    this._isLoading = true;
    this._error = null;

    try {
      const db = this._firebaseService.db;
      const storage = this._firebaseService.storage;
      
      // Get the comparison first to check for streams
      const comparisonRef = doc(db, 'comparisons', comparisonId);
      const comparisonSnap = await getDoc(comparisonRef);
      
      if (!comparisonSnap.exists()) {
        runInAction(() => {
          this._isLoading = false;
        });
        return false; // Comparison not found
      }
      
      const comparisonData = comparisonSnap.data() as Comparison;
      
      // Verify ownership (optional security check)
      if (comparisonData.userId !== userId) {
        console.error('User ID mismatch when deleting comparison');
        runInAction(() => {
          this._error = "Permission denied: user ID mismatch";
          this._isLoading = false;
        });
        return false;
      }
      
      // If streams exist in storage, delete them
      if (comparisonData.streamsStoragePath) {
        const streamsRef = ref(storage, comparisonData.streamsStoragePath);
        try {
          await deleteObject(streamsRef);
        } catch (error) {
          console.warn('Error deleting streams from storage:', error);
          // Continue with deletion even if storage deletion fails
        }
      }
      
      // Clear cache if it exists
      this._cachedStreams.delete(comparisonId);
      
      // Delete the comparison document
      await deleteDoc(comparisonRef);
      
      posthog.capture('comparison_deleted', { 
        comparison_id: comparisonId,
        plan_id: comparisonData.planId,
        user_id: userId
      });
      
      runInAction(() => {
        this._isLoading = false;
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting comparison:', error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture('error', { 
        source: 'deleteComparison', 
        error: (error as Error).message,
        comparison_id: comparisonId,
        user_id: userId
      });
      return false;
    }
  }

  /**
   * Delete a comparison by plan ID
   * @param planId Plan ID to delete the comparison for
   * @param userId User ID who owns the comparison
   */
  async deleteComparisonByPlanId(planId: string, userId: string): Promise<boolean> {
    this._isLoading = true;
    this._error = null;

    try {
      // First get the comparison to find its ID
      const comparison = await this.getComparisonByPlanId(planId, userId);
      
      if (!comparison || !comparison.id) {
        runInAction(() => {
          this._isLoading = false;
        });
        return false; // No comparison found
      }
      
      // Delete the comparison by its ID
      return await this.deleteComparison(comparison.id, userId);
    } catch (error) {
      console.error('Error deleting comparison by plan ID:', error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture('error', { 
        source: 'deleteComparisonByPlanId', 
        error: (error as Error).message,
        plan_id: planId,
        user_id: userId
      });
      return false;
    }
  }

  /**
   * Parse activity data from stored JSON string
   * @param comparison Comparison object containing stringified activity data
   * @returns Parsed Strava activity or null if parsing fails
   */
  getActivityFromComparison(comparison: Comparison): StravaActivity | null {
    try {
      return JSON.parse(comparison.activityData) as StravaActivity;
    } catch (error) {
      console.error('Error parsing activity data:', error);
      this._error = (error as Error).message;
      return null;
    }
  }

  /**
   * Get activity streams from a comparison
   * @param comparison Comparison object
   * @param userId User ID for analytics tracking
   * @returns Promise with parsed activity streams or null if not available/error
   */
  async getStreamsFromComparison(comparison: Comparison, userId: string): Promise<StravaActivityStreams | null> {
    if (!comparison.id || !comparison.streamsStoragePath) {
      return null;
    }
    
    try {
      // Check cache first
      if (this._cachedStreams.has(comparison.id)) {
        return this._cachedStreams.get(comparison.id)!;
      }
      
      this._isLoading = true;
      
      // Get streams from storage
      const storage = this._firebaseService.storage;
      const streamsRef = ref(storage, comparison.streamsStoragePath);
      const downloadUrl = await getDownloadURL(streamsRef);
      
      // Fetch the streams data
      const response = await fetch(downloadUrl);
      const streamsData = await response.text();
      
      // Parse the streams
      const streams = JSON.parse(streamsData) as StravaActivityStreams;
      
      // Cache the streams for future use
      this._cachedStreams.set(comparison.id, streams);
      
      posthog.capture('comparison_streams_loaded', {
        comparison_id: comparison.id,
        plan_id: comparison.planId,
        user_id: userId
      });
      
      runInAction(() => {
        this._isLoading = false;
      });
      
      return streams;
    } catch (error) {
      console.error('Error getting activity streams data:', error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture('error', {
        source: 'getStreamsFromComparison',
        error: (error as Error).message,
        comparison_id: comparison.id,
        user_id: userId
      });
      return null;
    }
  }
}